import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraEditorWorkspace } from '@mono/ui-components/admirals';
import { SpecraDocumentSurface } from '@mono/ui-components/captains';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { useFeatureFlag } from '../../contexts/featureFlagContext';
import { useProjectFiles } from '../../hooks/useProjectFiles';
import { useEditSession } from '../../pages/Editor/hooks/useEditSession';
import { CommentsRail, useDocumentComments } from './CommentsRail';
import { CompareDialog } from './CompareDialog';
import { ANCHOR_PATTERN, OfficeEditorFrame } from './OfficeEditorFrame';
import { OfficeWorkspaceAssistHostContext, OfficeWorkspaceAssistProvider } from './OfficeWorkspaceAssistContext';
import { PendingChangesBanner } from './PendingChangesBanner';
import { PresenceAvatars, presenceFromEvent } from './PresenceAvatars';
import { ReplaceFromFileDialog } from './ReplaceFromFileDialog';
import { statusText } from './statusText';
import { useOfficeMutations, useResponseWorkspace, useVersionViewSession } from './useOfficeWorkspace';
import { useVersionPolling } from './useVersionPolling';
import { VersionHistoryRail } from './VersionHistoryRail';
import { WorkspaceActionBar } from './WorkspaceActionBar';
import { workspaceBanners } from './WorkspaceBanners';
import { readStored, SIDE_COLLAPSED_KEY, SIDE_TAB_KEY, WorkspaceSideRail, writeStored } from './WorkspaceSideRail';
const MAIN_KINDS = new Set([
    'document',
    'response'
]);
function isDocx(name, contentType) {
    return /\.docx$/i.test(name) || contentType.includes('wordprocessingml');
}
/**
 * The writing workspace around one ONLYOFFICE editor (WRT-04): drafts (F1),
 * save status (F2), the Compare entry (F3), read-only versions (F4), History
 * (F12), the reload banners and the section jump. Pages (WRT-05 RFI, WRT-17
 * proposal) own the flag check and the header.
 */ export function OfficeWorkspace({ anchor, container, cta, documentId, header, hiddenSideTabs, initialSideTab, outline, onJumpToAnchor, pendingBanner, replaceSources, sideTabs, unitNoun, userNames }) {
    const { user } = useAuth();
    const [commentAnchor, setCommentAnchor] = useState();
    useEffect(()=>setCommentAnchor(undefined), [
        anchor,
        documentId
    ]);
    const editorAnchor = commentAnchor ?? anchor;
    const selfId = user?.id ?? '';
    const qc = useQueryClient();
    const [selectedId, setSelectedId] = useState(documentId);
    useEffect(()=>setSelectedId(documentId), [
        documentId
    ]);
    const workspace = useResponseWorkspace(container, selectedId);
    // An Assist apply from the chat card lands tracked changes server-side; refetch
    // so the pending-changes banner and version history catch up without waiting
    // for the next poll.
    const { refetch: refetchWorkspace } = workspace;
    useEffect(()=>{
        const onApplied = ()=>{
            void refetchWorkspace();
            void qc.invalidateQueries({
                queryKey: [
                    'officeVersions'
                ]
            });
        };
        window.addEventListener('shanks-document-applied', onApplied);
        return ()=>window.removeEventListener('shanks-document-applied', onApplied);
    }, [
        refetchWorkspace,
        qc
    ]);
    const data = workspace.data;
    const docs = data?.documents ?? [];
    const activeId = selectedId || data?.activeDocumentId || '';
    const activeDoc = docs.find((d)=>d.documentId === activeId);
    const mains = docs.filter((d)=>MAIN_KINDS.has(d.kind));
    const activeMain = activeDoc?.kind === 'alternate_draft' ? mains.find((m)=>m.documentId === activeDoc.parentDocumentId) : activeDoc;
    const alternates = docs.filter((d)=>d.kind === 'alternate_draft' && d.parentDocumentId === activeMain?.documentId);
    const recentVersions = data?.recentVersions ?? [];
    const caps = data?.capabilities;
    const canEdit = caps?.canEdit ?? false;
    const canWrite = canEdit && (caps?.writingOn ?? false);
    const currentVersionId = activeDoc?.currentVersionId ?? '';
    const currentVersion = recentVersions.find((v)=>v.id === currentVersionId);
    const m = useOfficeMutations(container);
    // ---- editor session + remounts ----
    const [viewingId, setViewingId] = useState(null);
    const [frameKey, setFrameKey] = useState(0);
    const [openedVersionId, setOpenedVersionId] = useState();
    const [saveState, setSaveState] = useState('idle');
    const [savedAt, setSavedAt] = useState();
    const [dirtySince, setDirtySince] = useState();
    const [presence, setPresence] = useState([]);
    const [readyKey, setReadyKey] = useState(null);
    const [everReady, setEverReady] = useState(false);
    const [frameError, setFrameError] = useState(false);
    const [restoreError, setRestoreError] = useState(null);
    // WRT-05: shirohige signs the bookmark jump into the config (action_link_bookmark).
    const editSession = useEditSession(activeId || undefined, editorAnchor && ANCHOR_PATTERN.test(editorAnchor) ? editorAnchor : '');
    const viewSession = useVersionViewSession(container, activeId || undefined, viewingId);
    // Switching to another document resets everything that belongs to the open
    // session. Not on the first document: the frame mounts in the same commit and
    // its child effects (e.g. an immediate onError) run before this one.
    const prevActiveId = useRef(activeId);
    useEffect(()=>{
        const prev = prevActiveId.current;
        prevActiveId.current = activeId;
        if (!prev || prev === activeId) return;
        setViewingId(null);
        setOpenedVersionId(undefined);
        setSaveState('idle');
        setSavedAt(undefined);
        setDirtySince(undefined);
        setPresence([]);
        setEverReady(false);
        setFrameError(false);
    }, [
        activeId
    ]);
    // The open session corresponds to the version that was current when it loaded.
    useEffect(()=>{
        if (editSession.data && !openedVersionId && currentVersionId) {
            setOpenedVersionId(currentVersionId);
        }
    }, [
        editSession.data,
        openedVersionId,
        currentVersionId
    ]);
    // Refetch the signed session first, then remount, so the new frame never
    // opens the previous document key.
    const remount = useCallback(async (versionId)=>{
        setOpenedVersionId(versionId);
        setSaveState('idle');
        setDirtySince(undefined);
        setFrameError(false);
        try {
            await qc.refetchQueries({
                queryKey: [
                    'editor-session',
                    activeId
                ]
            });
        } finally{
            setFrameKey((k)=>k + 1);
        }
    }, [
        qc,
        activeId
    ]);
    const adopt = useCallback((versionId)=>setOpenedVersionId(versionId), []);
    const polling = useVersionPolling({
        currentVersionId: viewingId ? undefined : currentVersionId,
        openedVersionId,
        dirty: saveState === 'dirty' || saveState === 'saving',
        dirtySince,
        recentVersions,
        selfId,
        onAdopt: adopt,
        onRemount: remount
    });
    const session = viewingId ? viewSession.data : editSession.data ?? undefined;
    // One identity for the mounted frame: it is the React key, and ready is
    // only true once THIS frame reported onDocumentReady.
    const frameId = `${activeId}:${viewingId ?? 'current'}:${editorAnchor ?? ''}:${frameKey}`;
    const sessionKey = session ? `${frameId}:${session.editorConfigJson}` : '';
    const ready = !!session && readyKey === sessionKey;
    const sessionError = viewingId ? viewSession.isError : editSession.isError;
    const surfaceState = frameError || sessionError ? 'error' : ready ? 'ready' : everReady ? 'reloading' : 'loading';
    const onStateChange = useCallback((ev)=>{
        const dirty = ev?.data;
        if (dirty === true) {
            setSaveState('dirty');
            setDirtySince((d)=>d ?? new Date());
        } else if (dirty === false) {
            setSaveState('saved');
            setSavedAt(new Date());
            setDirtySince(undefined);
        }
    }, []);
    const onInfo = useCallback((ev)=>{
        if (ev?.data?.mode === 'saving') setSaveState('saving');
    }, []);
    const onUsers = useCallback((ev)=>{
        const next = presenceFromEvent(ev, selfId);
        if (next) setPresence(next);
    }, [
        selfId
    ]);
    const onReady = useCallback(()=>{
        setReadyKey(sessionKey);
        setEverReady(true);
    }, [
        sessionKey
    ]);
    const onError = useCallback(()=>setFrameError(true), []);
    // ---- side rail ----
    const [sideCollapsed, setSideCollapsed] = useState(()=>readStored(SIDE_COLLAPSED_KEY) === '1');
    const collapse = (next)=>{
        setSideCollapsed(next);
        writeStored(SIDE_COLLAPSED_KEY, next ? '1' : '0');
    };
    // Controlled once something (WRT-11) asks for a tab; until then the rail owns it.
    const [sideTab, setSideTab] = useState();
    const openAssistTab = useCallback(()=>{
        setSideCollapsed(false);
        writeStored(SIDE_COLLAPSED_KEY, '0');
        setSideTab('assist');
        writeStored(SIDE_TAB_KEY, 'assist');
    }, []);
    const assistHost = useContext(OfficeWorkspaceAssistHostContext);
    const hasAssist = !!sideTabs?.assist;
    useEffect(()=>{
        if (!hasAssist || !assistHost) return;
        return assistHost.register(openAssistTab);
    }, [
        hasAssist,
        assistHost,
        openAssistTab
    ]);
    // ---- replace from file ----
    const [replaceOpen, setReplaceOpen] = useState(false);
    // WRT-31: version compare, behind the same flag as the rest of the workspace.
    const compareOn = useFeatureFlag('writing_workspace_v1') === true && !!currentVersionId;
    const [compare, setCompare] = useState({
        open: false
    });
    const projectFiles = useProjectFiles(container.kind === 'volume' ? container.projectId : null);
    const sources = useMemo(()=>{
        if (replaceSources) return replaceSources;
        if (container.kind !== 'volume') return [];
        return (projectFiles.data?.files ?? []).filter((f)=>isDocx(f.name, f.contentType)).map((f)=>({
                id: f.attachmentId,
                label: f.name,
                attachmentId: f.attachmentId
            }));
    }, [
        replaceSources,
        container.kind,
        projectFiles.data
    ]);
    const userName = useCallback((id)=>id === selfId ? 'You' : userNames?.get(id) ?? 'A teammate', [
        selfId,
        userNames
    ]);
    const status = statusText({
        alternate: activeDoc?.kind === 'alternate_draft',
        viewing: !!viewingId,
        saveState,
        current: currentVersion ?? (activeDoc ? {
            versionNumber: activeDoc.versionCount
        } : undefined),
        savedAt
    });
    const selectVersion = (versionId)=>{
        setRestoreError(null);
        setFrameError(false);
        setViewingId(versionId === currentVersionId ? null : versionId);
    };
    const restore = async ()=>{
        if (!viewingId || !activeId) return;
        setRestoreError(null);
        try {
            const res = await m.restoreVersion.mutateAsync({
                documentId: activeId,
                versionId: viewingId,
                baseVersionId: currentVersionId
            });
            setViewingId(null);
            void remount(res.version?.id ?? currentVersionId);
        } catch (err) {
            setRestoreError(err instanceof ConnectError && err.code === Code.Aborted ? 'Someone saved a newer version. Reload and try again.' : 'The version could not be restored.');
        }
    };
    const viewingVersion = viewingId ? recentVersions.find((v)=>v.id === viewingId) : undefined;
    // A plain call (no hooks inside) so an empty stack is null and the surface
    // draws no empty banner strip.
    const banners = workspaceBanners({
        canComment: caps?.canComment ?? false,
        canEdit: !data || canEdit,
        notice: polling.notice,
        onDismissNotice: polling.dismiss,
        onReload: polling.reload,
        pending: pendingBanner ?? ((data?.pendingRevisionCount ?? 0) > 0 ? /*#__PURE__*/ _jsx(PendingChangesBanner, {
            authors: data?.pendingRevisionAuthors,
            count: data?.pendingRevisionCount ?? 0
        }) : null),
        unitNoun,
        userName,
        viewing: viewingId ? {
            version: viewingVersion,
            canRestore: canWrite,
            error: restoreError,
            onBack: ()=>{
                setFrameError(false);
                setViewingId(null);
            },
            onRestore: ()=>void restore(),
            restoring: m.restoreVersion.isPending
        } : null
    });
    const commentsOn = caps?.writingOn ?? false;
    const comments = useDocumentComments(container, activeId, commentsOn);
    const resolvedSideTabs = sideTabs ? Object.fromEntries(Object.entries(sideTabs).map(([k, v])=>[
            k,
            typeof v === 'function' ? v({
                activeDocumentId: activeId,
                currentVersionId,
                remount
            }) : v
        ])) : undefined;
    const surface = /*#__PURE__*/ _jsx(SpecraDocumentSurface, {
        banner: banners,
        label: unitNoun === 'answer' ? 'Response document' : 'Volume document',
        mode: viewingId ? 'view' : 'edit',
        onReload: ()=>{
            if (!viewingId) return void remount(currentVersionId);
            setFrameError(false);
            setFrameKey((k)=>k + 1);
            void viewSession.refetch();
        },
        presence: viewingId ? undefined : /*#__PURE__*/ _jsx(PresenceAvatars, {
            users: presence
        }),
        state: surfaceState,
        title: activeDoc?.label,
        children: session && activeId ? /*#__PURE__*/ _jsx(OfficeEditorFrame, {
            anchor: viewingId ? undefined : editorAnchor,
            editorId: `office-editor-${activeId}`,
            onDocumentReady: onReady,
            onDocumentStateChange: viewingId ? undefined : onStateChange,
            onError: onError,
            onInfo: viewingId ? undefined : onInfo,
            onRequestUsers: viewingId ? undefined : onUsers,
            ready: ready,
            session: session,
            testId: Testids.officeWorkspace.frame
        }, frameId) : null
    });
    const actionBar = /*#__PURE__*/ _jsx(WorkspaceActionBar, {
        activeMain: activeMain,
        alternates: alternates,
        canEdit: canWrite,
        canExport: caps?.canExport ?? false,
        cta: cta,
        mainVersionNumber: activeMain && activeMain.documentId === activeDoc?.documentId ? currentVersion?.versionNumber : undefined,
        mains: mains,
        onArchiveDraft: async (draft)=>{
            await m.archiveDraft.mutateAsync({
                draftDocumentId: draft.documentId
            });
            setSelectedId(draft.parentDocumentId || undefined);
        },
        onCreateDraft: async ({ fromVersionId, name, operationId })=>{
            if (!activeMain) return;
            const res = await m.createDraft.mutateAsync({
                mainDocumentId: activeMain.documentId,
                name,
                fromVersionId,
                operationId
            });
            if (res.draft?.documentId) setSelectedId(res.draft.documentId);
        },
        budget: data?.budget && activeId === data.activeDocumentId && !viewingId ? data.budget : undefined,
        checkingExactPages: m.checkExactPages.isPending,
        onCheckExactPages: activeDoc?.documentType === 'docx' && !viewingId ? ()=>void m.checkExactPages.mutateAsync({
                documentId: activeId
            }).catch(()=>undefined) : undefined,
        onDownload: (format, stripComments)=>{
            if (!activeId) return;
            void m.downloadDocument.mutateAsync({
                documentId: activeId,
                format,
                stripComments
            }).then((res)=>res.url && window.location.assign(res.url));
        },
        preferredFormat: data?.preferredPackageFormat || undefined,
        onPromoteDraft: async (draft)=>{
            const main = mains.find((x)=>x.documentId === draft.parentDocumentId);
            await m.promoteDraft.mutateAsync({
                draftDocumentId: draft.documentId,
                mainBaseVersionId: main?.currentVersionId ?? '',
                archiveAfter: true,
                operationId: crypto.randomUUID()
            });
            setSelectedId(draft.parentDocumentId || undefined);
        },
        onRenameDraft: (draft, name)=>m.renameDraft.mutateAsync({
                draftDocumentId: draft.documentId,
                name
            }),
        onCompare: compareOn ? ()=>setCompare({
                open: true
            }) : undefined,
        onReplaceFromFile: sources.length > 0 ? ()=>setReplaceOpen(true) : undefined,
        onSelectDocument: (id)=>{
            setViewingId(null);
            setSelectedId(id);
        },
        onShowSide: sideCollapsed ? ()=>collapse(false) : undefined,
        recentVersions: recentVersions,
        selectedDocumentId: viewingId ? '' : activeId,
        status: status
    });
    return /*#__PURE__*/ _jsxs(OfficeWorkspaceAssistProvider, {
        onOpenAssistTab: openAssistTab,
        children: [
            /*#__PURE__*/ _jsx(SpecraEditorWorkspace, {
                actions: actionBar,
                "data-testid": Testids.officeWorkspace.workspace,
                header: header,
                outline: outline,
                side: /*#__PURE__*/ _jsx(WorkspaceSideRail, {
                    commentCount: comments.data ? comments.data.comments.filter((c)=>!c.done).length : data?.openCommentCount,
                    history: activeId ? /*#__PURE__*/ _jsx(VersionHistoryRail, {
                        canEdit: canWrite,
                        container: container,
                        currentVersionId: currentVersionId,
                        documentId: activeId,
                        onNameVersion: (label)=>m.labelVersion.mutateAsync({
                                documentId: activeId,
                                versionId: currentVersionId,
                                label,
                                kind: 'named'
                            }),
                        onCompareWithCurrent: compareOn ? (versionId)=>setCompare({
                                open: true,
                                fromVersionId: versionId
                            }) : undefined,
                        onSelectVersion: selectVersion,
                        selectedVersionId: viewingId ?? undefined,
                        userName: userName
                    }) : null,
                    hidden: [
                        ...hiddenSideTabs ?? [],
                        ...data && !commentsOn ? [
                            'comments'
                        ] : []
                    ],
                    initial: initialSideTab,
                    onChange: setSideTab,
                    onCollapse: ()=>collapse(true),
                    value: sideTab,
                    panels: {
                        ...resolvedSideTabs,
                        comments: commentsOn ? /*#__PURE__*/ _jsx(CommentsRail, {
                            canComment: caps?.canComment ?? false,
                            container: container,
                            documentId: activeId,
                            onChanged: (versionId)=>void remount(versionId),
                            onJumpToAnchor: onJumpToAnchor ?? setCommentAnchor,
                            sectionAnchor: editorAnchor
                        }) : undefined
                    }
                }),
                sideOpen: !sideCollapsed,
                children: surface
            }),
            compareOn && compare.open ? /*#__PURE__*/ _jsx(CompareDialog, {
                container: container,
                currentVersionId: currentVersionId,
                documentId: activeId,
                initialFromVersionId: compare.fromVersionId,
                onOpenChange: (open)=>setCompare((c)=>({
                            ...c,
                            open
                        })),
                open: true,
                otherDocuments: docs.filter((d)=>d.documentId !== activeId && (d.documentId === activeMain?.documentId || d.parentDocumentId === activeMain?.documentId))
            }) : null,
            /*#__PURE__*/ _jsx(ReplaceFromFileDialog, {
                onOpenChange: setReplaceOpen,
                onReplace: async (src)=>{
                    const res = await m.importDocx.mutateAsync({
                        documentId: activeId,
                        baseVersionId: currentVersionId,
                        attachmentId: src.attachmentId,
                        artifactId: src.artifactId,
                        artifactVersion: src.artifactVersion
                    });
                    await remount(res.version?.id ?? currentVersionId);
                },
                open: replaceOpen,
                sources: sources,
                unitNoun: unitNoun
            })
        ]
    });
}
